import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Drag-and-drop zone for the TCN ingestion flow.
 * - Light theme (NoiRock brand), minimalist.
 * - Accepts .xlsx files (Excel only). Falls back to a click-to-pick.
 * - Calls onFile(file) when the user drops or selects a file.
 */
const ExcelDropZone = ({ onFile, disabled = false }) => {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [hover, setHover] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback((files) => {
    if (!files || files.length === 0 || disabled) return;
    const f = files[0];
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      // Reject silently in UI feedback; parent can show an error if needed
      return;
    }
    onFile?.(f);
  }, [onFile, disabled]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  }, [disabled]);

  const onDragLeave = useCallback(() => setDragOver(false), []);

  const click = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  return (
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={click}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') click(); }}
      className={[
        'relative w-full rounded-2xl border-2 border-dashed transition-colors',
        'flex flex-col items-center justify-center gap-3 py-14 px-6 text-center cursor-pointer',
        disabled ? 'opacity-60 cursor-not-allowed' : '',
        dragOver
          ? 'border-primary bg-primary/5'
          : hover
            ? 'border-primary/70 bg-card'
            : 'border-border bg-card',
      ].join(' ')}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
      <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary/10 text-primary text-xl">
        ⬆
      </div>
      <div className="font-heading text-lg font-semibold text-foreground">
        {t('tcn.upload.drop.title', 'Drop your Excel file here')}
      </div>
      <div className="text-sm text-muted-foreground max-w-md">
        {t(
          'tcn.upload.drop.subtitle',
          'Accepts .xlsx files (TikTok creator data export). EN, FR, AR — auto-detected.',
        )}
      </div>
      <div className="text-xs text-muted-foreground mt-2">
        {t('tcn.upload.drop.or', 'or click to browse')}
      </div>
    </div>
  );
};

export default ExcelDropZone;
